import type { ErrorEvent } from "@sentry/react";

// Pure helpers for client error reporting (#1081). Kept in a plain .ts sibling
// (no Sentry/firebase runtime imports — the type-only import above is erased)
// so they unit-test without jsdom or the `import.meta` ts-jest trap.

export type AppRoute = "admin" | "site" | "demo" | "embed";

// Which sub-app a crash happened in, derived from the URL path. Mirrors the
// real routing in Root.tsx (STATIC_ROUTES /demo) + rootHooks.parseParams
// (/admin*, /embed*); everything else is the public site.
export const deriveRoute = (pathname: string): AppRoute => {
  if (pathname === "/demo") return "demo";
  if (pathname === "/embed" || pathname.startsWith("/embed/")) return "embed";
  if (pathname.startsWith("/admin")) return "admin";
  return "site";
};

// Matches an email-like substring. Used to redact addresses that may appear in
// free-text messages/tags (handles are scrubbed structurally below).
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

const redactEmails = (text: string): string =>
  text.replace(EMAIL_RE, "[email]");

// Strip PII from a Sentry event before it leaves the client. Keeps the user id
// (the Firebase uid — useful for correlation, not an email/handle) but drops
// email/username/ip_address; removes request cookies + headers (auth/session);
// redacts email-like substrings from the message and string tag values.
export const scrubPii = (event: ErrorEvent): ErrorEvent => {
  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : {};
  }

  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
  }

  if (typeof event.message === "string") {
    event.message = redactEmails(event.message);
  }

  if (event.tags) {
    for (const [key, value] of Object.entries(event.tags)) {
      if (typeof value === "string") {
        event.tags[key] = redactEmails(value);
      }
    }
  }

  return event;
};
